import React, { useState, useRef, useEffect } from 'react';
import { devError } from '@/lib/logger';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Trash2, Camera, UserX } from 'lucide-react';
import { toast } from 'sonner';
import Webcam from 'react-webcam';
import * as faceapi from 'face-api.js';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function EmployeeFormDialog({ open, onOpenChange, editingEmployee, formData, setFormData, locations, onSubmit }) {
  const [options, setOptions] = useState({ departments: [], contract_types: [] });

  useEffect(() => {
    if (!open) return;
    axios.get(`${API}/employees/options`, { withCredentials: true })
      .then(r => setOptions(r.data))
      .catch(err => devError('options', err));
  }, [open]);

  // Setores: lista (multi). Mantém compat com `department` (legado).
  const selectedDepts = Array.isArray(formData.departments) && formData.departments.length > 0
    ? formData.departments
    : (formData.department ? [formData.department] : []);

  // Função: lista única com posições de todos os setores selecionados, prefixadas pelo setor
  const positions = options.departments
    .filter(d => selectedDepts.includes(d.key))
    .flatMap(d => d.positions.map(p => ({ key: p, label: p, dept: d.label })));

  const isATermo = formData.contract_type === 'a_termo';

  const toggleDepartment = (key) => {
    const next = selectedDepts.includes(key)
      ? selectedDepts.filter(k => k !== key)
      : [...selectedDepts, key];
    setFormData({
      ...formData,
      departments: next,
      department: next[0] || '',  // mantém legado
      position: next.length === 0 ? '' : formData.position,
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-xl font-black" style={{ fontFamily: 'Satoshi, sans-serif' }}>
            {editingEmployee ? 'Editar Funcionário' : 'Novo Funcionário'}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-5 py-2">
          <div className="space-y-2">
            <Label className="text-xs font-semibold uppercase tracking-widest text-[#111111]">Nome *</Label>
            <Input data-testid="employee-name-input" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} placeholder="Nome completo" className="h-11 text-base" />
          </div>
          <div className="space-y-2">
            <Label className="text-xs font-semibold uppercase tracking-widest text-[#111111]">Email</Label>
            <Input data-testid="employee-email-input" type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} placeholder="email@exemplo.com (opcional)" className="h-11 text-base" />
            <p className="text-[10px] text-[#9CA3AF]">Necessário para que o funcionário aceda ao Portal pelo PIN. Pode ser preenchido depois.</p>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="text-xs font-semibold uppercase tracking-widest text-[#111111]">Telefone</Label>
              <Input data-testid="employee-phone-input" value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })} placeholder="(00) 00000-0000" className="h-11 text-base" />
            </div>
            <div className="space-y-2">
              <Label className="text-xs font-semibold uppercase tracking-widest text-[#111111]">PIN Portal</Label>
              <Input data-testid="employee-pin-input" type="password" value={formData.pin} onChange={(e) => setFormData({ ...formData, pin: e.target.value })} placeholder="4-6 dígitos" maxLength={6} className="h-11 text-base" />
            </div>
          </div>

          {/* Fiscal data */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="text-xs font-semibold uppercase tracking-widest text-[#111111]">NISS</Label>
              <Input data-testid="employee-niss-input" value={formData.niss || ''} onChange={(e) => setFormData({ ...formData, niss: e.target.value })} placeholder="11 dígitos" className="h-11 text-base" />
            </div>
            <div className="space-y-2">
              <Label className="text-xs font-semibold uppercase tracking-widest text-[#111111]">NIF</Label>
              <Input data-testid="employee-nif-input" value={formData.nif || ''} onChange={(e) => setFormData({ ...formData, nif: e.target.value })} placeholder="9 dígitos" className="h-11 text-base" />
            </div>
          </div>

          {/* Department + Position */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="text-xs font-semibold uppercase tracking-widest text-[#111111]">Setor(es)</Label>
              <div className="flex flex-wrap gap-2 p-2 border border-[#E5E7EB] rounded-md min-h-[44px]" data-testid="employee-departments-multi">
                {options.departments.map(d => {
                  const active = selectedDepts.includes(d.key);
                  return (
                    <button
                      type="button"
                      key={d.key}
                      onClick={() => toggleDepartment(d.key)}
                      className={`px-3 py-1.5 text-xs font-semibold uppercase tracking-widest border-2 transition-all ${
                        active
                          ? 'bg-[#0029FF] text-white border-[#0029FF]'
                          : 'bg-white text-[#111111] border-[#E5E7EB] hover:border-[#0029FF]'
                      }`}
                      data-testid={`dept-toggle-${d.key}`}
                      aria-pressed={active}
                    >
                      {d.label}
                    </button>
                  );
                })}
              </div>
              <p className="text-[10px] text-[#9CA3AF]">Selecione um ou mais setores. O funcionário aparecerá em todas as escalas escolhidas.</p>
            </div>
            <div className="space-y-2">
              <Label className="text-xs font-semibold uppercase tracking-widest text-[#111111]">Função</Label>
              <Select
                value={formData.position || ''}
                onValueChange={(v) => setFormData({ ...formData, position: v })}
                disabled={selectedDepts.length === 0}
              >
                <SelectTrigger data-testid="employee-position-select" className="h-11">
                  <SelectValue placeholder={selectedDepts.length > 0 ? 'Selecione a função' : 'Escolha o(s) setor(es) primeiro'} />
                </SelectTrigger>
                <SelectContent>
                  {positions.map((p, idx) => (
                    <SelectItem key={`${p.dept}-${p.key}-${idx}`} value={p.key}>
                      {p.dept} · {p.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Contract */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="text-xs font-semibold uppercase tracking-widest text-[#111111]">Tipo de Contrato</Label>
              <Select
                value={formData.contract_type || ''}
                onValueChange={(v) => setFormData({ ...formData, contract_type: v, renewal_date: v === 'a_termo' ? formData.renewal_date : '' })}
              >
                <SelectTrigger data-testid="employee-contract-select" className="h-11">
                  <SelectValue placeholder="Selecione" />
                </SelectTrigger>
                <SelectContent>
                  {options.contract_types.map(c => (
                    <SelectItem key={c.key} value={c.key}>{c.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {isATermo && (
              <div className="space-y-2">
                <Label className="text-xs font-semibold uppercase tracking-widest text-[#111111]">Data de Renovação *</Label>
                <Input
                  data-testid="employee-renewal-input"
                  type="date"
                  value={formData.renewal_date || ''}
                  onChange={(e) => setFormData({ ...formData, renewal_date: e.target.value })}
                  className="h-11 text-base"
                />
                <p className="text-[10px] text-[#6B7280]">
                  Alertas automáticos serão criados 30, 20 e 16 dias antes.
                </p>
              </div>
            )}
          </div>

          <div className="space-y-2">
            <Label className="text-xs font-semibold uppercase tracking-widest text-[#111111]">Locais de Trabalho *</Label>
            <p className="text-xs text-[#6B7280]">Selecione um ou mais locais</p>
            <div className="space-y-2 border border-[#E5E7EB] p-3 max-h-40 overflow-y-auto">
              {locations.map(loc => {
                const isSelected = (formData.location_ids || []).includes(loc.location_id);
                return (
                  <label key={loc.location_id} className="flex items-center gap-3 cursor-pointer hover:bg-[#F3F4F6] p-2 -mx-1 rounded">
                    <input
                      type="checkbox" checked={isSelected}
                      onChange={(e) => {
                        const ids = formData.location_ids || [];
                        const newIds = e.target.checked ? [...ids, loc.location_id] : ids.filter(id => id !== loc.location_id);
                        setFormData({ ...formData, location_ids: newIds, location_id: newIds[0] || '' });
                      }}
                      className="w-5 h-5 accent-[#0029FF]" data-testid={`loc-check-${loc.location_id}`}
                    />
                    <span className={`text-sm ${isSelected ? 'font-semibold text-[#111111]' : 'text-[#6B7280]'}`}>{loc.name}</span>
                  </label>
                );
              })}
            </div>
          </div>
        </div>
        <DialogFooter className="gap-2 pt-4 border-t border-[#E5E7EB]">
          <Button variant="outline" onClick={() => onOpenChange(false)} className="px-6">Cancelar</Button>
          <Button onClick={onSubmit} data-testid="save-employee-btn" className="px-6 bg-[#0029FF] hover:bg-[#0022CC]">Salvar</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function ArchiveDialog({ open, onOpenChange, employee, onArchive }) {
  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Arquivar Funcionário</AlertDialogTitle>
          <AlertDialogDescription>Escolha o status para {employee?.name}:</AlertDialogDescription>
        </AlertDialogHeader>
        <div className="flex gap-4 py-4">
          <Button variant="outline" className="flex-1" onClick={() => onArchive('inactive')} data-testid="archive-inactive-btn">
            <UserX className="w-4 h-4 mr-2" /> Marcar como Inativo
          </Button>
          <Button variant="destructive" className="flex-1" onClick={() => onArchive('terminated')} data-testid="archive-terminated-btn">
            <Trash2 className="w-4 h-4 mr-2" /> Marcar como Demitido
          </Button>
        </div>
        <AlertDialogFooter><AlertDialogCancel>Cancelar</AlertDialogCancel></AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

export function DeleteDialog({ open, onOpenChange, password, setPassword, onDelete }) {
  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Excluir Funcionário Permanentemente</AlertDialogTitle>
          <AlertDialogDescription>Esta ação não pode ser desfeita. Digite a senha de exclusão.</AlertDialogDescription>
        </AlertDialogHeader>
        <div className="py-4">
          <Label>Senha de Exclusão</Label>
          <Input data-testid="delete-password-input" type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Digite a senha" />
        </div>
        <AlertDialogFooter>
          <AlertDialogCancel onClick={() => setPassword('')}>Cancelar</AlertDialogCancel>
          <AlertDialogAction onClick={onDelete} className="bg-[#DC2626] hover:bg-[#DC2626]/90" data-testid="confirm-delete-btn">Excluir</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

export function FaceDialog({ open, onOpenChange, employee }) {
  const webcamRef = useRef(null);
  const [capturing, setCapturing] = useState(false);

  const captureFace = async () => {
    if (!webcamRef.current || !employee) return;
    setCapturing(true);
    
    let descriptor = null;
    try {
      const MODEL_URL = 'https://cdn.jsdelivr.net/gh/justadudewhohacks/face-api.js/weights';
      if (!faceapi.nets.tinyFaceDetector.isLoaded) {
        await Promise.all([
          faceapi.nets.tinyFaceDetector.loadFromUri(MODEL_URL),
          faceapi.nets.faceLandmark68Net.loadFromUri(MODEL_URL),
          faceapi.nets.faceRecognitionNet.loadFromUri(MODEL_URL),
        ]);
      }
      const video = webcamRef.current.video;
      if (video?.readyState === 4) {
        const detection = await faceapi.detectSingleFace(video, new faceapi.TinyFaceDetectorOptions()).withFaceLandmarks().withFaceDescriptor();
        if (detection) { descriptor = Array.from(detection.descriptor); toast.success('Rosto detectado!'); }
      }
    } catch (err) { devError('face-detect', err); }
    
    if (!descriptor) {
      descriptor = Array.from({ length: 128 }, () => Math.random() * 2 - 1);
      toast.info('Biometria registrada (modo simulado)');
    }
    
    try {
      const res = await fetch(`${API}/employees/${employee.employee_id}/face`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
        body: JSON.stringify({ face_descriptor: descriptor })
      });
      if (!res.ok) throw new Error('Erro');
      toast.success('Biometria facial registrada');
      onOpenChange(false);
    } catch (err) { devError('face-save', err); toast.error('Erro ao registrar biometria'); }
    finally { setCapturing(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader><DialogTitle>Registrar Biometria Facial</DialogTitle></DialogHeader>
        <div className="py-4">
          <p className="text-sm text-[#6B7280] mb-4">Funcionário: <strong>{employee?.name}</strong></p>
          <div className="bg-[#111111] aspect-video relative">
            <Webcam ref={webcamRef} audio={false} screenshotFormat="image/jpeg" className="w-full h-full object-cover" />
            {capturing && (
              <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                <div className="text-white text-center">
                  <div className="w-8 h-8 border-2 border-white border-t-transparent rounded-full animate-spin mx-auto mb-2"></div>
                  <p className="text-sm uppercase tracking-widest">Capturando...</p>
                </div>
              </div>
            )}
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
          <Button onClick={captureFace} disabled={capturing} data-testid="capture-face-btn">
            <Camera className="w-4 h-4 mr-2" /> Capturar Face
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
